/**
 * Library.jsx — fresh rebuild May 31 2026.
 *
 * User spec: strip the library down to the Journey-Breakdown layout —
 * three tabs (Ebooks · Audio Series · Vault), a single search bar, and
 * a flat list of tappable rows. No upload dropzones, no dropdown menus,
 * no reorder/edit affordances. Anything else gets added back later under
 * fresh instructions.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Headphones, Film, Lock, Search, Sparkles, ArrowLeft, ShieldPlus,
  Trophy, MessagesSquare, Pin, PinOff, History, ChevronRight, Layers,
  GraduationCap,
  BookOpenCheck,
  X as XIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { authFetch, checkAuth } from '../utils/authUtils';
import BookCover from '../components/BookCover';
import LibraryQuickDrop from '../components/LibraryQuickDrop';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMins = (totalSeconds) => {
  const s = Number(totalSeconds || 0);
  if (s <= 0) return '';
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const r = mins % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
};
const fmtNum = (n) => Number(n || 0).toLocaleString();

const PILL = {
  ebook:     { label: 'Ebook',     cls: 'bg-amber-400/10 text-amber-300 border-amber-400/30',     Icon: BookOpen },
  audiobook: { label: 'Audio',     cls: 'bg-fuchsia-400/10 text-fuchsia-300 border-fuchsia-400/30', Icon: Headphones },
  cinematic: { label: 'Cinematic', cls: 'bg-rose-400/10 text-rose-300 border-rose-400/30',         Icon: Film },
};

// June 2 2026 user spec — landing screen renders two BIG sharp tiles
// (Books / Audios). Counts are baked in. Tapping reveals the list
// (search + rows). Feb 9 2026 — re-skinned with the sharp aesthetic
// from `/reader-training`: dark card, eyebrow, big serif headline,
// asymmetric accent bar.
const BigPill = ({ id, label, count, Icon, accent, onClick }) => {
  // Each accent maps to one of the four jewel-tone families used across
  // the sharp design language. We store the full class strings literally
  // so Tailwind's JIT keeps them in production.
  const ACCENT = {
    amber:   { ring: 'border-amber-400/35   hover:border-amber-300/60   focus-visible:ring-amber-300/40',   text: 'text-amber-300',   bar: 'from-amber-400 to-amber-200',     iconBg: 'bg-amber-400/15  border-amber-400/30' },
    fuchsia: { ring: 'border-fuchsia-400/35 hover:border-fuchsia-300/60 focus-visible:ring-fuchsia-300/40', text: 'text-fuchsia-300', bar: 'from-fuchsia-400 to-fuchsia-200', iconBg: 'bg-fuchsia-400/15 border-fuchsia-400/30' },
    indigo:  { ring: 'border-indigo-400/35  hover:border-indigo-300/60  focus-visible:ring-indigo-300/40',  text: 'text-indigo-300',  bar: 'from-indigo-400 to-indigo-200',   iconBg: 'bg-indigo-400/15  border-indigo-400/30' },
    violet:  { ring: 'border-violet-400/35  hover:border-violet-300/60  focus-visible:ring-violet-300/40',  text: 'text-violet-300',  bar: 'from-violet-400 to-violet-200',   iconBg: 'bg-violet-400/15  border-violet-400/30' },
    cyan:    { ring: 'border-cyan-400/35    hover:border-cyan-300/60    focus-visible:ring-cyan-300/40',    text: 'text-cyan-300',    bar: 'from-cyan-400 to-cyan-200',       iconBg: 'bg-cyan-400/15    border-cyan-400/30' },
  }[accent] || { ring: 'border-white/10', text: 'text-white', bar: 'from-white to-white/60', iconBg: 'bg-white/10 border-white/20' };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`library-pill-${id}`}
      className={`group relative w-full min-h-36 text-left rounded-2xl overflow-hidden border ${ACCENT.ring} bg-card/90 shadow-[0_18px_50px_rgba(0,0,0,0.2)] active:scale-[0.995] motion-reduce:active:scale-100 transition-[transform,border-color,box-shadow] focus:outline-none focus-visible:ring-2`}
    >
      <div className="px-5 py-6 sm:px-7 sm:py-7 flex items-center gap-4 sm:gap-5">
        <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border ${ACCENT.iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-7 h-7 sm:w-8 sm:h-8 ${ACCENT.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`eyebrow ${ACCENT.text}`}>Tap to open</p>
          <p className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mt-1">{label}</p>
          {count != null && (
            <p className="text-[11px] sm:text-xs text-white/55 mt-1.5 font-medium tabular-nums">
              {count.toLocaleString()} {count === 1 ? 'item' : 'items'}
            </p>
          )}
        </div>
        <ChevronRight className={`w-5 h-5 ${ACCENT.text} opacity-60 group-hover:opacity-100 transition-opacity`} />
      </div>
      {/* Accent rail on the right edge — same trick the training page
          uses for its level card. Adds depth without resorting to
          gradients. */}
      <div className={`absolute right-0 top-0 h-full w-[3px] bg-gradient-to-b ${ACCENT.bar}`} />
    </button>
  );
};

const Row = ({ item, onOpen, xp, viewerCanSeeXp, isAdmin, onMoveToVault, vaultBusy, canPin, isPinned, onTogglePin, pinBusy }) => {
  const kind = item.type === 'ebook' ? 'ebook'
    : item.type === 'audiobook' ? 'audiobook'
    : 'cinematic';
  const P = PILL[kind];
  const isLocked = !!item.is_vault || (Number(item.price_amount || 0) > 0 && !item.is_free);
  const epCount = item.chapter_count || item.episode_count || item.episodes?.length || 0;
  const plays = item.plays || item.total_plays || item.play_count || 0;
  const rating = item.rating || item.average_rating || null;

  // The owner-only XP metric — words / minutes / XP earned — stays a
  // separate pill row beneath the title so the public metadata stays
  // clean for readers.
  const ownerMetric = viewerCanSeeXp ? (() => {
    if (kind === 'ebook') {
      return item.word_count ? `${fmtNum(item.word_count)}w` : null;
    }
    const secs = item.total_duration || item.duration_seconds || 0;
    return secs ? fmtMins(secs) : null;
  })() : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item, kind)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(item, kind); }
      }}
      className="w-full min-h-28 text-left flex items-center gap-3 p-2.5 sm:p-3 rounded-2xl bg-card/90 border border-white/10 shadow-[0_14px_36px_rgba(0,0,0,0.16)] hover:border-amber-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 cursor-pointer transition-colors"
      data-testid={`library-row-${item.series_id || item.book_id}`}
      aria-label={`Open ${item.title || 'Untitled'}`}
    >
      {/* Compact phone cover; larger thumbnail when space allows. */}
      <div className="relative w-16 h-20 sm:w-20 sm:h-24 rounded-xl overflow-hidden bg-[#0B0C10] border border-white/8 flex items-center justify-center flex-shrink-0">
        <BookCover
          book={item}
          className="h-full w-full"
        />
        {/* Type pill — uppercase eyebrow, jewel-tone border, no fill. */}
        <span
          className={`absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-[0.2em] border ${P.cls}`}
        >
          {P.label}
        </span>
        {isLocked && (
          <span
            className="absolute bottom-1 left-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9px] font-black uppercase tracking-[0.2em] border border-rose-400/40 bg-rose-400/10 text-rose-300"
            data-testid={`library-row-locked-${item.series_id || item.book_id}`}
          >
            <Lock className="w-2.5 h-2.5" />
            {item.is_vault ? 'Vault' : `£${Number(item.price_amount || 0).toFixed(2)}`}
          </span>
        )}
      </div>

      {/* Title + author + meta row — sharpened typography */}
      <div className="flex-1 min-w-0 py-1">
        <p className="font-display text-white font-black text-base sm:text-lg leading-tight line-clamp-2 tracking-tight">
          {item.title || 'Untitled'}
        </p>
        {item.author && (
          <p className="text-[11px] text-white/60 mt-0.5 truncate uppercase tracking-[0.15em] font-bold">
            {item.author}
          </p>
        )}
        <p className="text-[11px] text-white/45 mt-1 inline-flex items-center gap-1.5 flex-wrap tabular-nums">
          {kind !== 'ebook' && (
            <span className="font-bold text-white/70">
              {epCount} {epCount === 1 ? 'ep' : 'eps'}
            </span>
          )}
          {kind === 'ebook' && item.word_count > 0 && (
            <span className="font-bold text-white/70">{fmtNum(item.word_count)}w</span>
          )}
          {plays > 0 && (
            <>
              <span className="text-white/25">·</span>
              <span>{fmtNum(plays)} plays</span>
            </>
          )}
          {rating != null && Number(rating) > 0 && (
            <>
              <span className="text-white/25">·</span>
              <span className="inline-flex items-center gap-0.5 text-amber-300">
                <span>★</span>
                <span>{Number(rating).toFixed(1)}</span>
              </span>
            </>
          )}
        </p>
        {/* Owner-only metric + XP pill row — kept distinct from public meta. */}
        {(viewerCanSeeXp && (ownerMetric || (xp != null && xp > 0))) && (
          <p className="mt-1.5 inline-flex items-center gap-1.5 flex-wrap">
            {ownerMetric && (
              <span
                className="px-1.5 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-[0.2em] border-white/15 bg-white/5 text-white/70"
                data-testid={`library-row-metric-${item.series_id || item.book_id}`}
                title="Visible to the author and admins only"
              >
                {ownerMetric}
              </span>
            )}
            {xp != null && xp > 0 && (
              <span
                className="px-1.5 py-0.5 rounded-full border text-[10px] font-black uppercase tracking-[0.2em] border-amber-400/30 bg-amber-400/10 text-amber-300 inline-flex items-center gap-1"
                data-testid={`library-row-xp-${item.series_id || item.book_id}`}
                title="XP earned for this upload — visible to the author and admins only"
              >
                <Sparkles className="w-3 h-3" /> {fmtNum(xp)} XP
              </span>
            )}
          </p>
        )}
      </div>

      {/* Admin-only actions stay on the right edge */}
      {isAdmin && !item.is_vault && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveToVault?.(item, kind); }}
          disabled={vaultBusy}
          title="Move to Secret Vault (admin only)"
          aria-label="Move to Secret Vault"
          className="flex-shrink-0 ml-1 inline-flex items-center justify-center w-9 h-9 rounded-lg border border-rose-400/40 bg-rose-500/10 text-rose-200 hover:bg-rose-500/20 hover:text-rose-100 disabled:opacity-50 disabled:cursor-wait focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/60"
          data-testid={`library-row-move-to-vault-${item.series_id || item.book_id}`}
        >
          <ShieldPlus className="w-4 h-4" />
        </button>
      )}
      {canPin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTogglePin?.(item); }}
          disabled={pinBusy}
          title={isPinned ? 'Unpin from Top Series' : 'Pin to Top Series (admin/co-admin)'}
          aria-label={isPinned ? 'Unpin from Top Series' : 'Pin to Top Series'}
          className={`flex-shrink-0 ml-1 inline-flex items-center justify-center w-9 h-9 rounded-lg border focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 disabled:opacity-50 disabled:cursor-wait ${
            isPinned
              ? 'border-amber-300/70 bg-amber-400/20 text-amber-200 hover:bg-amber-400/30'
              : 'border-slate-500/40 bg-slate-700/40 text-slate-200 hover:bg-slate-600/40'
          }`}
          data-testid={`library-row-pin-${item.series_id || item.book_id}`}
        >
          {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
      )}
    </div>
  );
};

// ── Features Rail (Jun 3 2026) ──────────────────────────────────────
// "Last Read" pin only — Top Series was moved to the landing gate
// (Feb 6 2026 user spec: "Don't put top series inside audiobook or
// ebook but in library on top of the two").
// Renders nothing if the last-read pin is missing.
// Continue Reading / Continue Listening rail. Feb 9 2026 user spec:
// the gate shows both an ebook rail ("Continue Reading") AND an audio
// rail ("Continue Listening") side-by-side when the user has progress
// on either. Inside the list view we render just the relevant one.
// Items are horizontally scrolling cards with progress %.
const ContinueRail = ({ items, label, eyebrowLabel, accent, onOpen, onDismiss, kind, testid }) => {
  if (!items || items.length === 0) return null;
  const ACCENT = {
    indigo: { text: 'text-indigo-300', iconBg: 'bg-indigo-400/10 border-indigo-400/30', bar: 'bg-indigo-400', border: 'hover:border-indigo-400/40 focus-visible:ring-indigo-400/40' },
    fuchsia: { text: 'text-fuchsia-300', iconBg: 'bg-fuchsia-400/10 border-fuchsia-400/30', bar: 'bg-fuchsia-400', border: 'hover:border-fuchsia-400/40 focus-visible:ring-fuchsia-400/40' },
  }[accent] || { text: 'text-white', iconBg: 'bg-white/10 border-white/20', bar: 'bg-white', border: 'hover:border-white/30 focus-visible:ring-white/40' };
  const Icon = kind === 'audio' ? Headphones : BookOpen;

  return (
    <section className="mb-5" data-testid={testid}>
      <p className={`eyebrow ${ACCENT.text} mb-2 inline-flex items-center gap-1.5`}>
        <Icon className="w-3 h-3" /> {eyebrowLabel || label}
      </p>
      <div className="flex gap-2.5 overflow-x-auto pb-2 -mx-1 px-1 snap-x no-scrollbar">
        {items.map((it) => {
          const sid = it.series_id || it.book_id || it.content_id;
          const targetKind = (kind === 'ebook')
            ? (it.book_id && !it.series_id ? 'ebook' : 'series')
            : 'series';
          const pct = Math.max(0, Math.min(100, Number(it.progress || 0)));
          return (
            <div
              key={sid}
              className={`flex-shrink-0 w-44 sm:w-48 rounded-xl overflow-hidden bg-[#12131A] border border-white/8 ${ACCENT.border} focus-within:ring-2 transition-colors relative`}
              data-testid={`${testid}-card-${sid}`}
            >
              {onDismiss && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(it, targetKind);
                  }}
                  className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full bg-black/60 hover:bg-red-500/80 text-white grid place-items-center backdrop-blur-sm transition-colors"
                  aria-label="Remove from Continue rail"
                  title="Remove from this rail (progress is kept)"
                  data-testid={`${testid}-dismiss-${sid}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onOpen(it, targetKind)}
                className="w-full text-left focus:outline-none"
                data-testid={`${testid}-open-${sid}`}
              >
                <div className="relative w-full aspect-[16/10] bg-[#0B0C10] flex items-center justify-center overflow-hidden">
                  <BookCover book={it} className="h-full w-full" />
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/60">
                    <div
                      className={`h-full ${ACCENT.bar} transition-[width]`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="p-2.5">
                  <p className="font-display text-[13px] font-black text-white truncate tracking-tight">
                    {it.title || 'Untitled'}
                  </p>
                  <p className={`text-[10px] uppercase tracking-[0.2em] font-black mt-0.5 ${ACCENT.text} tabular-nums`}>
                    {pct}% {kind === 'audio' ? 'listened' : 'read'}
                  </p>
                </div>
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
};

// Top-Series horizontal scroll rendered on the Library landing gate,
// ABOVE the two big pills (Feb 6 2026 user spec). Only renders when
// the admin has actually pinned at least one series.
const TopSeriesRail = ({ featured, onOpen }) => {
  const top = featured?.top_series || [];
  if (top.length === 0) return null;

  const cardOf = (item) => {
    if (!item) return null;
    const kind = item.book_id && !item.series_id ? 'ebook' : 'series';
    const sid = item.series_id || item.book_id;
    return (
      <button
        key={sid}
        type="button"
        onClick={() => onOpen(item, kind)}
        className="flex-shrink-0 w-36 sm:w-40 rounded-xl overflow-hidden bg-[#12131A] border border-white/8 hover:border-amber-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 text-left transition-colors"
        data-testid={`library-rail-card-${sid}`}
      >
        <div className="w-full aspect-[3/4] bg-[#0B0C10] flex items-center justify-center overflow-hidden">
          <BookCover book={item} className="h-full w-full" />
        </div>
        <div className="p-2">
          <p className="font-display text-[13px] font-black text-white truncate tracking-tight">{item.title || 'Untitled'}</p>
          {item.author && (
            <p className="text-[10px] text-white/45 truncate mt-0.5 uppercase tracking-[0.15em]">{item.author}</p>
          )}
        </div>
      </button>
    );
  };

  return (
    <section className="mb-6" data-testid="library-top-series-rail">
      <p className="eyebrow text-amber-300 mb-2 flex items-center gap-1.5">
        <Pin className="w-3 h-3" /> Top Series
      </p>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x no-scrollbar">
        {top.map(cardOf)}
      </div>
    </section>
  );
};

// Academic Library live-preview rail (Feb 13 2026 user spec — "you have
// not wired Academic to library"). Pulled from /api/academic-library
// (latest approved items, both kinds). Self-hides when empty.
const AcademicPicksRail = ({ items, onOpen }) => {
  if (!items || items.length === 0) return null;
  return (
    <section className="mb-6" data-testid="library-academic-picks-rail">
      <div className="flex items-center justify-between mb-2">
        <p className="eyebrow text-violet-300 flex items-center gap-1.5">
          <GraduationCap className="w-3 h-3" /> Academic Picks
        </p>
        <button
          type="button"
          onClick={() => onOpen(null, 'academic-all')}
          className="text-[10px] uppercase tracking-[0.25em] font-black text-violet-200/70 hover:text-violet-200"
          data-testid="library-academic-picks-see-all"
        >
          See all →
        </button>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x no-scrollbar">
        {items.map((it) => {
          return (
            <button
              key={it.item_id}
              type="button"
              onClick={() => onOpen(it, 'academic')}
              className="flex-shrink-0 w-36 sm:w-40 rounded-xl overflow-hidden bg-[#12131A] border border-white/8 hover:border-violet-400/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/40 text-left transition-colors"
              data-testid={`library-academic-picks-card-${it.item_id}`}
            >
              <div className="w-full aspect-[3/4] bg-[#0B0C10] flex items-center justify-center overflow-hidden">
                <BookCover
                  book={it}
                  author={it.author_name}
                  className="h-full w-full"
                />
              </div>
              <div className="p-2">
                <p className="font-display text-[13px] font-black text-white truncate tracking-tight">
                  {it.title || 'Untitled'}
                </p>
                <p className="text-[10px] text-white/45 truncate mt-0.5 uppercase tracking-[0.15em]">
                  {it.kind === 'publication' ? 'Publication' : 'Project'}
                  {it.author_name ? ` · ${it.author_name}` : ''}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const Library = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [loadVersion, setLoadVersion] = useState(0);
  const [books, setBooks] = useState([]);
  const [series, setSeries] = useState([]);
  // June 2 2026 user spec — Library lands on a 2-pill gate. Tab is null
  // until the reader picks one; selecting unlocks the list view below.
  const [tab, setTab] = useState(null);
  const [query, setQuery] = useState('');
  // June 2 2026 — viewer-aware XP overlay. Writers + admins fetch the
  // per-content XP map so each row can show the words/minutes + XP
  // earned pill next to its own uploads. Readers never see this; the
  // endpoint itself is role-gated.
  const [me, setMe] = useState(null);
  const [xpItems, setXpItems] = useState({});
  const [xpSeries, setXpSeries] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) {
        setLoading(true);
        setLoadError('');
      }
      try {
        const u = await checkAuth();
        if (!u) { navigate('/login'); return; }
        if (!cancelled) setMe(u);
        const r = await authFetch(`${BACKEND_URL}/api/library`);
        if (!r.ok) throw new Error(`Library request failed (${r.status})`);
        const d = await r.json();
        if (cancelled) return;
        setBooks(d.books || []);
        const folded = [
          ...(d.series || []),
          ...((d.standalone_books || []).filter((b) => b.type === 'audiobook' || b.type === 'cinematic')
            .map((b) => ({
              ...b,
              series_id: b.book_id,
              chapter_count: 1,
              episodes: [{ episode_id: b.book_id, chapter_id: b.book_id }],
            }))),
        ];
        setSeries(folded);
      } catch (error) {
        console.error('Failed to load library:', error);
        if (!cancelled) setLoadError('We couldn’t load your library. Check your connection and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }

      // Fetch the XP map only when the viewer has access to it. The
      // endpoint enforces role itself, but skipping the call for readers
      // keeps the network tab clean.
      if (u.role === 'writer' || u.role === 'admin') {
        try {
          const r2 = await authFetch(`${BACKEND_URL}/api/writer/content-xp`);
          if (r2.ok && !cancelled) {
            const xj = await r2.json();
            setXpItems(xj.items || {});
            setXpSeries(xj.series || {});
          }
        } catch { /* non-fatal */ }
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, loadVersion]);

  const isWriter = me?.role === 'writer';
  const isAdmin = me?.role === 'admin';
  const isCoadmin = me?.role === 'coadmin' || me?.role === 'king';
  const canPin = isAdmin || isCoadmin;
  const [vaultBusyId, setVaultBusyId] = useState(null);
  // Features Rail (Jun 3 2026) — Last Read pin + admin-curated Top Series.
  // Jun 4 2026 — adds `continue_reading` (last 6 in-progress titles)
  // surfaced as a horizontal rail above Books / Audios.
  const [featured, setFeatured] = useState({ top_series: [], last_read: null, continue_reading: [], continue_listening: [], book_series_count: 0, academic_picks: [], academic_count: 0 });
  const [pinBusyId, setPinBusyId] = useState('');
  const refreshFeatured = async () => {
    try {
      const [fRes, sRes, aRes] = await Promise.allSettled([
        authFetch(`${BACKEND_URL}/api/library/featured`),
        authFetch(`${BACKEND_URL}/api/text-series`),
        authFetch(`${BACKEND_URL}/api/academic-library?limit=6`),
      ]);
      const d = fRes.status === 'fulfilled' && fRes.value.ok ? await fRes.value.json() : {};
      const sd = sRes.status === 'fulfilled' && sRes.value.ok ? await sRes.value.json() : { series: [] };
      const ad = aRes.status === 'fulfilled' && aRes.value.ok ? await aRes.value.json() : { items: [], count: 0 };
      setFeatured({
        top_series: d.top_series || [],
        last_read: d.last_read || null,
        continue_reading:   d.continue_reading   || [],
        continue_listening: d.continue_listening || [],
        book_series_count: (sd.series || []).length,
        academic_picks: (ad.items || []).slice(0, 6),
        academic_count: Number(ad.count || (ad.items || []).length || 0),
      });
    } catch { /* non-fatal */ }
  };
  useEffect(() => { refreshFeatured(); }, []);
  const togglePin = async (item) => {
    const sid = item.series_id || item.book_id;
    if (!sid || !canPin) return;
    const isPinned = featured.top_series.some((s) => (s.series_id || s.book_id) === sid);
    setPinBusyId(sid);
    try {
      const url = `${BACKEND_URL}/api/admin/library/featured/pin/${encodeURIComponent(sid)}`;
      const r = await authFetch(url, { method: isPinned ? 'DELETE' : 'POST' });
      if (r.ok) {
        await refreshFeatured();
      }
    } finally { setPinBusyId(''); }
  };

  // Admin-only: flip is_vault=true on a Library item. Posts to the
  // existing admin endpoint (same one AdminVaultManager + SeriesFolder
  // use) and optimistically removes the row, since Vault items are
  // intentionally hidden from /library (June 2 2026 vault-seal spec).
  const moveToVault = async (item, kind) => {
    const cid = kind === 'ebook'
      ? (item.book_id || item.content_id)
      : (item.series_id || item.book_id);
    if (!cid) return;
    const label = item.title || 'this item';
    if (!window.confirm(
      `Move "${label}" to the Secret Vault?\n\nIt will be hidden from the public library and only visible inside /secret-vault.`,
    )) {
      return;
    }
    setVaultBusyId(cid);
    const tid = toast.loading(`Moving "${label}" to Vault…`);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/vault/content/${encodeURIComponent(cid)}`, {
        method: 'POST',
        body: JSON.stringify({ is_vault: true }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Move to Vault failed.', { id: tid });
        return;
      }
      // Drop the row from local state so the list snaps shut immediately.
      if (kind === 'ebook') {
        setBooks((prev) => prev.filter((b) => (b.book_id || b.content_id) !== cid));
      } else {
        setSeries((prev) => prev.filter((s) => (s.series_id || s.book_id) !== cid));
      }
      toast.success(`✓ "${label}" moved to Vault.`, { id: tid });
    } catch {
      toast.error('Network error moving to Vault.', { id: tid });
    } finally {
      setVaultBusyId(null);
    }
  };

  // Split into the three tabs. Search is a simple title/author contains.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (it) => {
      if (!q) return true;
      return (it.title || '').toLowerCase().includes(q)
        || (it.author || '').toLowerCase().includes(q);
    };
    // Vault content is deliberately EXCLUDED from the Library — it is
    // only viewable through /secret-vault after the PIN gate + per-device
    // bind. (June 2 2026 — user spec: "restore vault security; library
    // shouldn't expose vault items at all".)
    const ebooks = books.filter((b) => b.type === 'ebook' && !b.is_vault).filter(match);
    const audio  = series.filter((s) => !s.is_vault).filter(match);
    return { ebooks, audio };
  }, [books, series, query]);

  // Iter 80 — dismiss a series from Continue Reading/Listening without
  // wiping its progress. Backend sets a `dismissed_from_rail_at` on the
  // user_progress row(s); the featured endpoint filters those out.
  const dismissFromRail = async (item) => {
    const sid = item.series_id || item.book_id || item.content_id;
    if (!sid) return;
    // Optimistic UI — drop it from the local rail immediately so the
    // interaction feels instant. Rollback on failure.
    const prev = featured;
    setFeatured((f) => f ? ({
      ...f,
      continue_reading: (f.continue_reading || []).filter(x => (x.series_id||x.book_id||x.content_id) !== sid),
      continue_listening: (f.continue_listening || []).filter(x => (x.series_id||x.book_id||x.content_id) !== sid),
    }) : f);
    try {
      const r = await authFetch(
        `${BACKEND_URL}/api/library/continue-rail/dismiss/${sid}`,
        { method: 'POST' },
      );
      if (!r.ok) throw new Error('dismiss failed');
      toast.success('Removed from Continue rail');
    } catch (_e) {
      setFeatured(prev);
      toast.error('Could not remove — please retry.');
    }
  };

  const open = (item, kind) => {
    if (kind === 'academic-all') {
      navigate('/academic-library');
      return;
    }
    if (kind === 'academic') {
      // Academic items don't have a standalone detail route yet — drop
      // the reader straight into the grid so they can read & earn.
      navigate('/academic-library');
      return;
    }
    if (kind === 'ebook') {
      navigate(`/reader/${item.book_id}`);
      return;
    }
    // June 2 2026 — Series rows now route to the dedicated SeriesPage
    // (/series/:id) so the listener gets the episode list, played-state
    // indicators, and auto-resume to last-played. The player launches
    // from inside SeriesPage once they pick an episode.
    const sid = item.series_id || item.book_id;
    if (!sid) return;
    navigate(`/series/${sid}`);
  };

  if (loading) {
    return (
      <main className="min-h-[60vh] bg-background px-4 py-8 sm:px-6" aria-busy="true" aria-live="polite">
        <div className="mx-auto max-w-7xl animate-pulse motion-reduce:animate-none">
          <div className="h-4 w-28 rounded bg-white/10" />
          <div className="mt-4 h-12 w-64 max-w-full rounded bg-white/10" />
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            <div className="h-40 rounded-2xl border border-white/5 bg-card/70" />
            <div className="h-40 rounded-2xl border border-white/5 bg-card/70" />
          </div>
          <p className="sr-only">Loading library</p>
        </div>
      </main>
    );
  }

  if (loadError) {
    return (
      <main className="min-h-[60vh] bg-background px-4 py-10 sm:px-6 grid place-items-center">
        <section className="w-full max-w-lg rounded-2xl border border-white/10 bg-card p-6 sm:p-8 text-center shadow-2xl" role="alert">
          <BookOpen className="mx-auto h-10 w-10 text-violet-300" aria-hidden="true" />
          <h1 className="mt-4 font-display text-3xl font-black text-white">Your library is out of reach</h1>
          <p className="mt-3 text-sm leading-6 text-white/65">{loadError}</p>
          <button
            type="button"
            onClick={() => setLoadVersion((version) => version + 1)}
            className="mt-6 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-violet-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
            data-testid="library-retry"
          >
            Try again
          </button>
        </section>
      </main>
    );
  }

  const items = tab ? filtered[tab === 'ebooks' ? 'ebooks' : 'audio'] : [];

  // ── Landing screen (gate) — TWO big pills, nothing else. ─────────
  if (!tab) {
    const isWriterOrAdmin = me?.role === 'writer' || me?.role === 'admin';
    return (
      <main className="relative overflow-hidden bg-background mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 pb-32 min-h-[calc(100vh-120px)]" data-testid="library-page">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top_left,rgba(109,40,217,0.2),transparent_58%)]" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl">
        {/* Global drag-drop overlay — writers/admins can drop audio/
            video files anywhere on the Library and route them straight
            into one of their series. Same component is reused on the
            Scribe Journey page so there's one single "upload channel". */}
        <LibraryQuickDrop enabled={isWriterOrAdmin} />

        {/* Sharp header — eyebrow + serif title, mirrors the Reading
            Training page. Replaces the old icon+H1 line. */}
        <header className="mb-8 max-w-3xl" data-testid="library-header">
          <p className="eyebrow text-amber-300">Your reading room</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.02] mt-2">
            Library<span className="text-amber-300">.</span>
          </h1>
          <p className="mt-3 max-w-2xl text-sm sm:text-base leading-7 text-white/60">
            Find a title, return to a book in progress, or choose another part of your collection.
          </p>
        </header>

        {/* Feb 2026 user update: Top Series removed from the gate.
            Continue Reading / Continue Listening moved to the BOTTOM
            of the gate so the main pills lead the page. */}

        {/* Feb 13 2026 — Academic Picks rail. Live preview of latest
            approved Projects/Publications. Self-hides when empty. Tap a
            card → /academic-library. */}
        <AcademicPicksRail items={featured.academic_picks} onOpen={open} />

        <div className="flex-1 space-y-8" data-testid="library-pill-gate">
          <section aria-labelledby="library-collections-heading">
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <p className="eyebrow text-violet-300">Browse</p>
                <h2 id="library-collections-heading" className="font-display text-2xl font-black text-white sm:text-3xl">Collections</h2>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 sm:gap-4">
          <BigPill
            id="ebooks"
            label="Ebooks"
            count={filtered.ebooks.length}
            Icon={BookOpen}
            accent="amber"
            onClick={() => setTab('ebooks')}
          />
          <BigPill
            id="audio"
            label="Audios"
            count={filtered.audio.length}
            Icon={Headphones}
            accent="fuchsia"
            onClick={() => setTab('audio')}
          />

          {/* Feb 12 2026 fix: user asked for Book Series to live DIRECTLY
              under Audios, sized like its siblings (Ebooks/Audios). The
              old 2-col row that paired Book Series with an unsolicited
              "Audio Series · Listen in drops" pill was removed entirely
              ("Remove listen in drops. I didnt ask you to do that"). */}
          <BigPill
            id="book-series"
            label="Book Series"
            count={featured?.book_series_count || 0}
            Icon={Layers}
            accent="indigo"
            onClick={() => navigate('/book-series')}
          />

          {/* Feb 13 2026 — Academic Library wired into the main Library
              gate per user spec ("you have not wired Academic to library").
              Sits directly under Book Series as a sibling top-level kind. */}
          <BigPill
            id="academic-library"
            label="Academic Library"
            count={featured?.academic_count || 0}
            Icon={GraduationCap}
            accent="violet"
            onClick={() => navigate('/academic-library')}
          />

          {/* Feb 13 2026 — LEARN (reader-skills drills) wired into the
              Library gate per user spec ("add to all profile a LEARN in
              the profile and library"). Cyan accent + BookOpenCheck
              icon to distinguish it from Academic Library's violet
              GraduationCap. Count omitted — drills are behavioural, not
              content-count based. */}
          <BigPill
            id="learn"
            label="LEARN"
            count={null}
            Icon={BookOpenCheck}
            accent="cyan"
            onClick={() => navigate('/learn')}
          />
            </div>
          </section>

          {/* Smaller dual pills — Quest Board + Book Club. Sharpened
              from gradient blocks to dark cards with eyebrow + serif
              label + accent dot. Feb 9 2026. */}
          <section aria-label="Library destinations" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => navigate('/quests')}
              className="rounded-2xl border border-emerald-400/30 bg-[#12131A] hover:border-emerald-400/60 px-4 py-4 text-left active:scale-[0.995] transition-[border-color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
              data-testid="library-pill-quests"
            >
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-emerald-400/15 border border-emerald-400/30 grid place-items-center">
                  <Trophy className="w-3.5 h-3.5 text-emerald-300" />
                </span>
                <p className="eyebrow text-emerald-300">Quest Board</p>
              </div>
              <p className="font-display text-lg font-black text-white mt-2 leading-tight">Hit weekly goals</p>
              <p className="text-[11px] text-white/55 mt-0.5">Earn coins</p>
            </button>
            <button
              type="button"
              onClick={() => navigate('/book-club')}
              className="rounded-2xl border border-sky-400/30 bg-[#12131A] hover:border-sky-400/60 px-4 py-4 text-left active:scale-[0.995] transition-[border-color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40"
              data-testid="library-pill-bookclub"
            >
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-sky-400/15 border border-sky-400/30 grid place-items-center">
                  <MessagesSquare className="w-3.5 h-3.5 text-sky-300" />
                </span>
                <p className="eyebrow text-sky-300">Book Club</p>
              </div>
              <p className="font-display text-lg font-black text-white mt-2 leading-tight">Join the convo</p>
              <p className="text-[11px] text-white/55 mt-0.5">Reader chat</p>
            </button>
          </section>

          {/* My Library — premium personal pocket library. Private to
              the viewer (backend filters by user_id). Sharpened. */}
          <button
            type="button"
            onClick={() => navigate('/my-library')}
            className="rounded-2xl border border-amber-400/30 bg-[#12131A] hover:border-amber-400/60 px-4 py-4 text-left active:scale-[0.995] transition-[border-color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40"
            data-testid="library-pill-my-library"
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-lg bg-amber-400/15 border border-amber-400/30 grid place-items-center">
                  <ShieldPlus className="w-3.5 h-3.5 text-amber-300" />
                </span>
                <p className="eyebrow text-amber-300">My Library</p>
              </div>
              <span className="text-[9px] uppercase tracking-[0.25em] font-black text-amber-200/80">Premium · Private</span>
            </div>
            <p className="font-display text-lg font-black text-white mt-2 leading-tight">Type, paste or scan</p>
            <p className="text-[11px] text-white/55 mt-0.5">Your pocket library — only you can see it</p>
          </button>

          {/* Feb 2026 user spec: Continue Reading / Listening now sit at
              the BOTTOM of the gate. Each rail hides itself if empty so
              first-time readers don't see empty rails. */}
          <div className="grid gap-5 lg:grid-cols-2">
          <ContinueRail
            items={featured.continue_reading}
            eyebrowLabel="Continue Reading"
            accent="indigo"
            kind="ebook"
            onOpen={open}
            onDismiss={dismissFromRail}
            testid="library-continue-reading"
          />
          <ContinueRail
            items={featured.continue_listening}
            eyebrowLabel="Continue Listening"
            accent="fuchsia"
            kind="audio"
            onOpen={open}
            onDismiss={dismissFromRail}
            testid="library-continue-listening"
          />
          </div>
        </div>
        </div>
      </main>
    );
  }

  // ── List view (after a pill is tapped) ───────────────────────────
  const tabMeta = tab === 'ebooks'
    ? { label: 'Ebooks', Icon: BookOpen, ring: 'border-amber-400/40', dot: 'text-amber-300' }
    : { label: 'Audios', Icon: Headphones, ring: 'border-fuchsia-400/40', dot: 'text-fuchsia-300' };
  const TabIcon = tabMeta.Icon;

  return (
    <main className="bg-background max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8 pb-32 min-h-screen" data-testid="library-page">
      {/* Header with back-to-gate */}
      <header className="mb-6 rounded-2xl border border-white/10 bg-card/80 p-4 sm:p-6 shadow-[0_18px_55px_rgba(0,0,0,0.2)]">
        <button
          type="button"
          onClick={() => { setTab(null); setQuery(''); }}
          className="text-white/60 hover:text-white inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
          data-testid="library-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Library
        </button>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className={`eyebrow ${tabMeta.dot}`}>Collection</p>
            <h1 className="mt-1 font-display text-4xl font-black text-white sm:text-5xl">{tabMeta.label}</h1>
          </div>
          <p className="text-sm text-white/50 tabular-nums" aria-live="polite">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
        </div>
      </header>

      {/* Sharp search — pill-shaped dark input, no shadow, eyebrow icon. */}
      <div className="relative mb-6 max-w-2xl" data-testid="library-search">
        <Search className="w-4 h-4 text-white/40 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true" />
        <label htmlFor="library-search-input" className="sr-only">Search {tabMeta.label.toLowerCase()} by title or author</label>
        <input
          id="library-search-input"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title or author…"
          className="w-full bg-card border border-white/10 rounded-xl text-white text-sm pl-11 pr-12 py-3.5 placeholder:text-white/35 focus:outline-none focus:border-amber-400/60 focus-visible:ring-2 focus-visible:ring-amber-300/40"
          data-testid="library-search-input"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-white/50 hover:bg-white/10 hover:text-white"
            aria-label="Clear library search"
          >
            <XIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {/* Feb 9 2026 — Continue Reading rail on ebooks tab, Continue
          Listening rail on audio tab. Each is wired to the matching
          backend list (ebooks → continue_reading, audio → continue_listening). */}
      {tab === 'ebooks' && (
        <ContinueRail
          items={featured.continue_reading}
          eyebrowLabel="Continue Reading"
          accent="indigo"
          kind="ebook"
          onOpen={open}
          testid="library-continue-reading"
        />
      )}
      {tab === 'audio' && (
        <ContinueRail
          items={featured.continue_listening}
          eyebrowLabel="Continue Listening"
          accent="fuchsia"
          kind="audio"
          onOpen={open}
          testid="library-continue-listening"
        />
      )}

      {/* List */}
      {items.length === 0 ? (
        <section className="rounded-2xl border border-dashed border-white/15 bg-card/50 px-5 py-12 text-center" data-testid="library-empty" aria-live="polite">
          <TabIcon className={`mx-auto h-9 w-9 ${tabMeta.dot}`} aria-hidden="true" />
          <h2 className="mt-4 font-display text-2xl font-black text-white">
            {query ? 'No matches found' : (tab === 'ebooks' ? 'No books yet' : 'No audio series yet')}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-white/55">
            {query ? `Nothing in this collection matches “${query}”.` : 'Items will appear here when they are available to your account.'}
          </p>
          {query && (
            <button type="button" onClick={() => setQuery('')} className="mt-5 rounded-lg border border-white/15 px-4 py-2 text-sm font-bold text-white hover:bg-white/10">
              Clear search
            </button>
          )}
        </section>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="library-list">
          {items.map((it) => {
            // Admins see XP for any author; scribes see XP only on their
            // own uploads. Owner check uses user_id stamped on each item.
            const isOwner = isAdmin || (isWriter && it.user_id && it.user_id === me?.user_id);
            const xp = tab === 'ebooks'
              ? xpItems[it.book_id || it.content_id]
              : xpSeries[it.series_id || it.book_id];
            return (
              <Row
                key={it.series_id || it.book_id}
                item={it}
                onOpen={open}
                xp={xp}
                viewerCanSeeXp={isOwner}
                isAdmin={isAdmin}
                onMoveToVault={moveToVault}
                vaultBusy={vaultBusyId === (it.series_id || it.book_id || it.content_id)}
                canPin={canPin}
                isPinned={featured.top_series.some((s) => (s.series_id || s.book_id) === (it.series_id || it.book_id))}
                onTogglePin={togglePin}
                pinBusy={pinBusyId === (it.series_id || it.book_id)}
              />
            );
          })}
        </div>
      )}
    </main>
  );
};

export default Library;
